import { c as _c } from "react/compiler-runtime";
import { getErrorMessage, postHostedOnboardingSkipPasskeyRegisterMutation } from '@onefootprint/fetch-hosted';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { IcoPasskey40, IcoWarning40 } from '@onefootprint/icons';
import type { ActionKind, RegisterPasskeyAttemptContext } from '@onefootprint/request-types';
import { BottomSheet, Button, LinkButton, PageShell } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DeviceInfo } from '@/idv2/utils/get-device';
import { getLogger } from '@/idv2/utils/logger';
import useBiometricInit, { unwrapPasskeyError } from './use-register-biometric';
import getSkipReason from './utils/get-skip-reason';
type RegisterPasskeyProps = {
  deviceInfo: DeviceInfo;
  isInIframe: boolean;
  token: string;
  actionKind: ActionKind;
  onComplete: () => void;
  // The auth app's handoff registers a passkey outside any onboarding, so there is nothing for
  // skip_passkey_register to skip. It reports the D2P session as canceled instead.
  onSkip?: () => void;
};
const {
  logInfo,
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'liveness-register-passkey'
});

// Must match idv's FPCustomEvents.receivedDeviceResponseJson: idv's router listens for it and
// forwards the response to the webview redirect's sdk_args_token.
const DEVICE_RESPONSE_EVENT = 'receivedDeviceResponseJson';
const RegisterPasskey = t0 => {
  const $ = _c(92);
  const {
    deviceInfo,
    isInIframe,
    token,
    actionKind,
    onComplete,
    onSkip
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "liveness.register"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("idv2", t1);
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = postHostedOnboardingSkipPasskeyRegisterMutation();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const skipLivenessMutation = useMutation(t2);
  const biometricInitMutation = useBiometricInit();
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [passkeyRegisterAttempts, setPasskeyRegisterAttempts] = useState(t3);
  const showRequestErrorToast = useRequestErrorToast();
  let t4;
  if ($[3] !== actionKind || $[4] !== biometricInitMutation || $[5] !== onComplete || $[6] !== token) {
    t4 = async () => {
      ;
      try {
        const {
          deviceResponseJson
        } = await biometricInitMutation.mutateAsync({
          token,
          actionKind
        });
        document.dispatchEvent(new CustomEvent(DEVICE_RESPONSE_EVENT, {
          detail: {
            deviceResponseJson
          }
        }));
        setTimeout(() => {
          logInfo("Passkeys registration succeeded");
          onComplete();
        }, 2500);
      } catch (t5) {
        const error = t5;
        const {
          cause,
          elapsedTimeInOsPromptMs
        } = unwrapPasskeyError(error);
        const errorMessage = getErrorMessage(cause);
        logWarn(`Failed to register passkeys for user: ${errorMessage}`, cause);
        setPasskeyRegisterAttempts(prev => [...prev, {
          errorMessage,
          elapsedTimeInOsPromptMs
        }]);
      }
    };
    $[3] = actionKind;
    $[4] = biometricInitMutation;
    $[5] = onComplete;
    $[6] = token;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleRegister = t4;
  let t5;
  if ($[8] !== deviceInfo || $[9] !== isInIframe || $[10] !== onComplete || $[11] !== onSkip || $[12] !== passkeyRegisterAttempts || $[13] !== showRequestErrorToast || $[14] !== skipLivenessMutation || $[15] !== token) {
    t5 = async () => {
      logInfo("Skipping liveness after retrying registering passkeys");
      if (onSkip) {
        onSkip();
        return;
      }
      ;
      try {
        await skipLivenessMutation.mutateAsync({
          headers: {
            "X-Fp-Authorization": token
          },
          body: {
            context: {
              reason: getSkipReason({
                deviceInfo,
                isInIframe,
                isUnavailable: false
              }),
              clientType: "web",
              numAttempts: passkeyRegisterAttempts.length,
              attempts: passkeyRegisterAttempts
            }
          }
        });
        onComplete();
      } catch (t6) {
        const error_0 = t6;
        showRequestErrorToast(error_0);
        logWarn("Failed to skip liveness after retrying registering passkeys", error_0);
      }
    };
    $[8] = deviceInfo;
    $[9] = isInIframe;
    $[10] = onComplete;
    $[11] = onSkip;
    $[12] = passkeyRegisterAttempts;
    $[13] = showRequestErrorToast;
    $[14] = skipLivenessMutation;
    $[15] = token;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const handleSkip = t5;
  let t6;
  bb0: {
    if (biometricInitMutation.isSuccess) {
      let t7;
      if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <IcoPasskey40 color="primary" />;
        $[17] = t7;
      } else {
        t7 = $[17];
      }
      let t8;
      if ($[18] !== t) {
        t8 = t(_temp);
        $[18] = t;
        $[19] = t8;
      } else {
        t8 = $[19];
      }
      let t9;
      if ($[20] !== t) {
        t9 = t(_temp2);
        $[20] = t;
        $[21] = t9;
      } else {
        t9 = $[21];
      }
      let t10;
      if ($[22] !== t8 || $[23] !== t9) {
        t10 = {
          icon: t7,
          headerTitle: t8,
          headerSubtitle: t9
        };
        $[22] = t8;
        $[23] = t9;
        $[24] = t10;
      } else {
        t10 = $[24];
      }
      t6 = t10;
      break bb0;
    }
    if (passkeyRegisterAttempts.length) {
      let t7;
      if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <IcoWarning40 color="error" />;
        $[25] = t7;
      } else {
        t7 = $[25];
      }
      let t8;
      if ($[26] !== t) {
        t8 = t(_temp3);
        $[26] = t;
        $[27] = t8;
      } else {
        t8 = $[27];
      }
      let t9;
      if ($[28] !== t) {
        t9 = t(_temp4);
        $[28] = t;
        $[29] = t9;
      } else {
        t9 = $[29];
      }
      let t10;
      if ($[30] !== t) {
        t10 = t(_temp5);
        $[30] = t;
        $[31] = t10;
      } else {
        t10 = $[31];
      }
      let t11;
      if ($[32] !== t) {
        t11 = t(_temp6);
        $[32] = t;
        $[33] = t11;
      } else {
        t11 = $[33];
      }
      let t12;
      if ($[34] !== t10 || $[35] !== t11 || $[36] !== t8 || $[37] !== t9) {
        t12 = {
          icon: t7,
          headerTitle: t8,
          headerSubtitle: t9,
          primaryButtonText: t10,
          secondaryButtonText: t11
        };
        $[34] = t10;
        $[35] = t11;
        $[36] = t8;
        $[37] = t9;
        $[38] = t12;
      } else {
        t12 = $[38];
      }
      t6 = t12;
      break bb0;
    }
    let t7;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <IcoPasskey40 color="primary" />;
      $[39] = t7;
    } else {
      t7 = $[39];
    }
    let t8;
    if ($[40] !== actionKind || $[41] !== t) {
      t8 = actionKind === "replace" ? t(_temp7) : t(_temp8);
      $[40] = actionKind;
      $[41] = t;
      $[42] = t8;
    } else {
      t8 = $[42];
    }
    let t9;
    if ($[43] !== t) {
      t9 = t(_temp9);
      $[43] = t;
      $[44] = t9;
    } else {
      t9 = $[44];
    }
    let t10;
    if ($[45] !== t) {
      t10 = t(_temp10);
      $[45] = t;
      $[46] = t10;
    } else {
      t10 = $[46];
    }
    let t11;
    if ($[47] !== t10 || $[48] !== t8 || $[49] !== t9) {
      t11 = {
        icon: t7,
        headerTitle: t8,
        headerSubtitle: t9,
        primaryButtonText: t10
      };
      $[47] = t10;
      $[48] = t8;
      $[49] = t9;
      $[50] = t11;
    } else {
      t11 = $[50];
    }
    t6 = t11;
  }
  const {
    icon,
    headerTitle,
    headerSubtitle,
    primaryButtonText,
    secondaryButtonText
  } = t6;
  let t7;
  if ($[51] !== biometricInitMutation.isPending || $[52] !== handleRegister || $[53] !== primaryButtonText || $[54] !== skipLivenessMutation) {
    t7 = primaryButtonText && <Button loading={biometricInitMutation.isPending} disabled={biometricInitMutation.isPending || skipLivenessMutation.isPending} onClick={handleRegister} fullWidth={true} size="large">{primaryButtonText}</Button>;
    $[51] = biometricInitMutation.isPending;
    $[52] = handleRegister;
    $[53] = primaryButtonText;
    $[54] = skipLivenessMutation;
    $[55] = t7;
  } else {
    t7 = $[55];
  }
  let t8;
  if ($[56] !== biometricInitMutation.isPending || $[57] !== handleSkip || $[58] !== secondaryButtonText || $[59] !== skipLivenessMutation) {
    t8 = secondaryButtonText && <Button loading={skipLivenessMutation.isPending} disabled={biometricInitMutation.isPending || skipLivenessMutation.isPending} onClick={handleSkip} fullWidth={true} size="large" variant="secondary">{secondaryButtonText}</Button>;
    $[56] = biometricInitMutation.isPending;
    $[57] = handleSkip;
    $[58] = secondaryButtonText;
    $[59] = skipLivenessMutation;
    $[60] = t8;
  } else {
    t8 = $[60];
  }
  let t9;
  if ($[61] !== biometricInitMutation.isSuccess || $[62] !== t) {
    t9 = !biometricInitMutation.isSuccess && <div className="flex justify-center items-center pt-2"><LinkButton onClick={() => setShowBottomSheet(true)}>{t(_temp11)}</LinkButton></div>;
    $[61] = biometricInitMutation.isSuccess;
    $[62] = t;
    $[63] = t9;
  } else {
    t9 = $[63];
  }
  let t10;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setShowBottomSheet(false);
    $[64] = t10;
  } else {
    t10 = $[64];
  }
  let t11;
  if ($[65] !== t) {
    t11 = t(_temp12);
    $[65] = t;
    $[66] = t11;
  } else {
    t11 = $[66];
  }
  let t12;
  if ($[67] !== t) {
    t12 = t(_temp13);
    $[67] = t;
    $[68] = t12;
  } else {
    t12 = $[68];
  }
  let t13;
  if ($[69] !== t12) {
    t13 = <p className="text-body-2 text-primary whitespace-pre-wrap">{t12}</p>;
    $[69] = t12;
    $[70] = t13;
  } else {
    t13 = $[70];
  }
  let t14;
  if ($[71] !== t) {
    t14 = t(_temp14);
    $[71] = t;
    $[72] = t14;
  } else {
    t14 = $[72];
  }
  let t15;
  if ($[73] !== t14) {
    t15 = <p className="text-body-2 text-primary whitespace-pre-wrap">{t14}</p>;
    $[73] = t14;
    $[74] = t15;
  } else {
    t15 = $[74];
  }
  let t16;
  if ($[75] !== t13 || $[76] !== t15) {
    t16 = <div className="flex flex-col gap-3 mb-5">{t13}{t15}</div>;
    $[75] = t13;
    $[76] = t15;
    $[77] = t16;
  } else {
    t16 = $[77];
  }
  let t17;
  if ($[78] !== showBottomSheet || $[79] !== t11 || $[80] !== t16) {
    t17 = <BottomSheet open={showBottomSheet} onClose={t10} title={t11} portalId="passkeys-register-container">{t16}</BottomSheet>;
    $[78] = showBottomSheet;
    $[79] = t11;
    $[80] = t16;
    $[81] = t17;
  } else {
    t17 = $[81];
  }
  let t18;
  if ($[82] !== t17 || $[83] !== t7 || $[84] !== t8 || $[85] !== t9) {
    t18 = <div className="flex flex-col gap-3 text-center items-center justify-center" id="passkeys-register-container">{t7}{t8}{t9}{t17}</div>;
    $[82] = t17;
    $[83] = t7;
    $[84] = t8;
    $[85] = t9;
    $[86] = t18;
  } else {
    t18 = $[86];
  }
  let t19;
  if ($[87] !== headerSubtitle || $[88] !== headerTitle || $[89] !== icon || $[90] !== t18) {
    t19 = <PageShell title={headerTitle} subtitle={headerSubtitle} headerIcon={icon}>{t18}</PageShell>;
    $[87] = headerSubtitle;
    $[88] = headerTitle;
    $[89] = icon;
    $[90] = t18;
    $[91] = t19;
  } else {
    t19 = $[91];
  }
  return t19;
};
export default RegisterPasskey;
function _temp($) {
  return $.success.title;
}
function _temp2($_0) {
  return $_0.success.subtitle;
}
function _temp3($_1) {
  return $_1.retry.title;
}
function _temp4($_2) {
  return $_2.retry.subtitle;
}
function _temp5($_3) {
  return $_3.retry.cta;
}
function _temp6($_4) {
  return $_4.retry.skip;
}
function _temp7($_5) {
  return $_5.titleReplace;
}
function _temp8($_6) {
  return $_6.titleAdd;
}
function _temp9($_7) {
  return $_7.subtitle;
}
function _temp10($_8) {
  return $_8.cta;
}
function _temp11($_9) {
  return $_9.learnMore.cta;
}
function _temp12($_10) {
  return $_10.learnMore.title;
}
function _temp13($_11) {
  return $_11.learnMore.content1;
}
function _temp14($_12) {
  return $_12.learnMore.content2;
}